import { CircularProgress, MenuItem, Stack } from '@mui/material';
import { useAdminPortalConfig } from 'api/adminPortalConfig';
import LoginFlowView from 'app/Views/LoginFlowView';
import NoAuthView from 'app/Views/NoAuthView';
import TenantSelectorView from 'app/Views/TenantSelectorView';
import { ROUTES, Segments } from 'app/Views/routes';
import EllipsesTypography from 'components/EllipsesTypography';
import { useNav } from 'app/navigation';
import { fillSegments } from 'modules/route/route';
import React, { ReactNode, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useCanAccessAdminPortal } from 'state/access';
import { useTenantBootstrap } from 'state/tenantBootstrap';
import { useHasMultipleTenants, useTenant } from 'state/user';
import { useSelectedWidgetByRoute, useWidgets } from 'state/widget/widget';
import { NoAccessReason } from 'types/access';
import AppLayout from './AppLayout';

interface IAuthProviderAppLayoutProps {
	children?: ReactNode;
}

const AuthProviderAppLayout: React.FC<IAuthProviderAppLayoutProps> = ({
	children = null
}) => {
	const navigate = useNav();

	const { data: appConfig } = useAdminPortalConfig();
	const [canAccess, noAccessReason] = useCanAccessAdminPortal();
	const { needsSelection } = useTenant();
	const hasMultipleTenants = useHasMultipleTenants();
	const isOnTenantSelectRoute = useMatch(ROUTES.tenantSelect);

	const { bootstrapping } = useTenantBootstrap();

	const widgets = useWidgets();
	const routeWidget = useSelectedWidgetByRoute();

	// Single tenant on /select falls through to AppRoutes, which has no /select
	// route and redirects to the first widget.
	const showTenantSelector =
		noAccessReason === NoAccessReason.NEEDS_TENANT_SELECTION ||
		(!!isOnTenantSelectRoute && hasMultipleTenants);

	const content = useMemo(() => {
		if (showTenantSelector) {
			if (bootstrapping) {
				return (
					<Stack
						data-testid="tenant-selector-loading"
						direction="column"
						alignItems="center"
						justifyContent="center"
						height="100%"
					>
						<CircularProgress />
					</Stack>
				);
			}
			return <TenantSelectorView />;
		}
		if (canAccess) {
			return children;
		}
		/* istanbul ignore next  */
		if (noAccessReason === NoAccessReason.LOADING) {
			return null;
		}
		const loginFlowId = appConfig?.loginFlowId || 'sign-in';
		if (noAccessReason === NoAccessReason.USER_NOT_LOGGED_IN) {
			return (
				<LoginFlowView flowId={loginFlowId} styleId={appConfig?.styleId} />
			);
		}
		return <NoAuthView noAccessReason={noAccessReason} />;
	}, [
		showTenantSelector,
		bootstrapping,
		canAccess,
		appConfig,
		children,
		noAccessReason
	]);

	return (
		<AppLayout
			sideBar={
				!needsSelection &&
				canAccess &&
				routeWidget &&
				widgets?.map((widget) => (
					<MenuItem
						data-testid="sidebar-menu-item"
						data-widget-id={widget.id}
						key={widget.id}
						sx={{
							height: 43
						}}
						selected={routeWidget?.id === widget.id}
						onClick={() =>
							navigate(
								fillSegments(ROUTES.widget, {
									[Segments.WidgetPath]: widget.path
								})
							)
						}
					>
						<EllipsesTypography>{widget.name}</EllipsesTypography>
					</MenuItem>
				))
			}
		>
			{content}
		</AppLayout>
	);
};

export default AuthProviderAppLayout;
